/**
 * Whether the location can get freight on and off a truck.
 *
 * A dock, or failing that a forklift — and if neither, the shipment needs a
 * lift gate, which changes the price, so the screen can't just continue. The
 * questions are skipped entirely when the quote already includes a lift gate.
 *
 * Answers live on the shipment in redux rather than local state because the
 * requote-with-lift-gate flow reads them back out.
 */

import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { LoadingCapabilitiesAvailability } from "@freightsimple/generated-dashboard-openapi-client/models/LoadingCapabilitiesAvailability";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "@/store";
import {
  setCurrentScreenIsDirty,
  setDeliveryLocationHasDock,
  setDeliveryLocationHasForklift,
  setPickupLocationHasDock,
  setPickupLocationHasForklift,
} from "@/Slices/bookShipmentSlice";

interface BookShipmentAddressLoadingCapabilitiesInput {
  context: LocationContext;
  location: PreQuoteLocation;
  matchingSavedLocation: SavedLocation | undefined;
}

export function useBookShipmentAddressLoadingCapabilities(
  input: BookShipmentAddressLoadingCapabilitiesInput,
) {
  const { context, location, matchingSavedLocation } = input;
  const dispatch = useDispatch();
  const isPickup = context === LocationContext.Pickup;

  const locationHasDock = useSelector((s: RootState) =>
    isPickup
      ? s.bookShipment.shipment.pickupLocationHasDock
      : s.bookShipment.shipment.deliveryLocationHasDock,
  );

  const locationHasForklift = useSelector((s: RootState) =>
    isPickup
      ? s.bookShipment.shipment.pickupLocationHasForklift
      : s.bookShipment.shipment.deliveryLocationHasForklift,
  );

  const hasLiftGate = location.accessorials.includes(
    "LOGISTICS_LIFT_GATE_REQUIRED",
  );

  function onDockChange(value: LoadingCapabilitiesAvailability) {
    dispatch(setCurrentScreenIsDirty());
    if (isPickup) {
      dispatch(setPickupLocationHasDock(value));
    } else {
      dispatch(setDeliveryLocationHasDock(value));
    }
  }

  function onForkliftChange(value: LoadingCapabilitiesAvailability) {
    dispatch(setCurrentScreenIsDirty());
    if (isPickup) {
      dispatch(setPickupLocationHasForklift(value));
    } else {
      dispatch(setDeliveryLocationHasForklift(value));
    }
  }

  useEffect(
    function () {
      if (matchingSavedLocation) {
        const savedDock = matchingSavedLocation.locationHasDock;
        const savedForklift = matchingSavedLocation.locationHasForklift;

        // Only update if values are provided and current Redux values are undefined or different
        if (savedDock !== undefined) {
          if (
            !locationHasDock ||
            locationHasDock === LoadingCapabilitiesAvailability.Unknown ||
            locationHasDock !== savedDock
          ) {
            onDockChange(savedDock);
          }
        }
        if (savedForklift !== undefined) {
          if (
            !locationHasForklift ||
            locationHasForklift === LoadingCapabilitiesAvailability.Unknown ||
            locationHasForklift !== savedForklift
          ) {
            onForkliftChange(savedForklift);
          }
        }
      }
    },
    [
      matchingSavedLocation?.savedLocationId,
      matchingSavedLocation?.locationHasDock,
      matchingSavedLocation?.locationHasForklift,
      // Note: locationHasDock and locationHasForklift are intentionally NOT in dependencies
      // to avoid infinite loops. This effect should only run when matchingSavedLocation changes.
    ],
  );

  function dockError(): string | undefined {
    // If lift gate is present, we don't need to ask about dock
    if (hasLiftGate) {
      return undefined;
    }

    if (
      !locationHasDock ||
      locationHasDock === LoadingCapabilitiesAvailability.Unknown
    ) {
      return "Please select if this location has a shipping dock";
    }
    return undefined;
  }

  function forkliftError(): string | undefined {
    // If lift gate is present, we don't need to ask about forklift
    if (hasLiftGate) {
      return undefined;
    }

    if (locationHasDock === LoadingCapabilitiesAvailability.No) {
      if (
        !locationHasForklift ||
        locationHasForklift === LoadingCapabilitiesAvailability.Unknown
      ) {
        return "Please select if this location has a forklift";
      }
    }
    return undefined;
  }

  // Check if both dock and forklift are "No" - requires lift gate
  function needsLiftGate(): boolean {
    if (hasLiftGate) {
      return false;
    }

    // If we don't have dock or forklift then we need a liftgate
    return (
      locationHasDock === LoadingCapabilitiesAvailability.No &&
      locationHasForklift === LoadingCapabilitiesAvailability.No
    );
  }

  return {
    locationHasDock,
    locationHasForklift,
    hasLiftGate,
    onDockChange,
    onForkliftChange,
    dockError,
    forkliftError,
    needsLiftGate,
  };
}
